import { useEffect, useState } from "react";
import { Autocomplete } from "@/components/Autocomplete";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { getRecentAuftraege, searchAuftraege, type AuftragLookup } from "@/lib/material";
import {
  getProjectRejectPreview,
  type ProjectRejectHoursAction,
  type ProjectRejectPayload,
  type ProjectRejectPreview,
} from "@/lib/projects";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

function formatHours(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  const text = rounded.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${text} ${rounded === 1 ? "Stunde" : "Stunden"}`;
}

function hoursQuestion(preview: ProjectRejectPreview): string {
  const bits: string[] = [];
  if (preview.hours_decimal > 0) bits.push(formatHours(preview.hours_decimal));
  if (preview.timesheet_count === 1) bits.push("1 Stundennachweis");
  else if (preview.timesheet_count > 1) bits.push(`${preview.timesheet_count} Stundennachweise`);
  if (preview.timer_count === 1) bits.push("eine laufende Zeiterfassung");
  else if (preview.timer_count > 1) bits.push(`${preview.timer_count} laufende Zeiterfassungen`);
  if (!bits.length) {
    return "Auf diesem Projekt sind Stunden gebucht. Was soll mit den Stunden passieren?";
  }
  return `Auf diesem Projekt sind ${bits.join(", ")} gebucht. Was soll mit den Stunden passieren?`;
}

export function ProjectRejectDialog({
  open,
  projectId,
  auftragsnummer,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  projectId: number;
  auftragsnummer?: string | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: (payload: ProjectRejectPayload) => void;
}) {
  const [begruendung, setBegruendung] = useState("");
  const [action, setAction] = useState<ProjectRejectHoursAction | null>(null);
  const [target, setTarget] = useState<AuftragLookup | null>(null);
  const [preview, setPreview] = useState<ProjectRejectPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewNonce, setPreviewNonce] = useState(0);

  useEffect(() => {
    if (!open) return;
    setBegruendung("");
    setAction(null);
    setTarget(null);
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null);
    setPreviewError("");
    setPreviewLoading(true);
    void getProjectRejectPreview(projectId)
      .then((row) => {
        if (!cancelled) setPreview(row);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(
            err instanceof Error ? err.message : "Stunden konnten nicht gelesen werden.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, previewNonce]);

  const trimmed = begruendung.trim();
  const hasHours = Boolean(preview?.has_hours);
  const needsTarget = hasHours && action === "umbuchen";
  const canSubmit =
    trimmed.length > 0 &&
    !previewLoading &&
    !previewError &&
    (!hasHours || (action != null && (!needsTarget || target != null)));

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onClose}
      dirty={trimmed.length > 0 || action != null || target != null}
      size="md"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      plainBackdrop
      title="Projekt ablehnen"
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} />
          <Button
            type="button"
            variant="danger"
            disabled={!canSubmit}
            loading={loading}
            onClick={() =>
              onConfirm({
                begruendung: trimmed,
                stunden_aktion: hasHours ? action : null,
                ziel_auftrag_id: needsTarget ? target?.id ?? null : null,
              })
            }
          >
            Ablehnen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm leading-relaxed text-label-secondary">
          {auftragsnummer ? (
            <>
              Projekt <span className="font-semibold text-label">{auftragsnummer}</span> auf
              Abgelehnt setzen.
            </>
          ) : (
            <>Projekt auf Abgelehnt setzen.</>
          )}{" "}
          Das gilt unabhängig vom aktuellen Status. Die Begründung wird in die Projektnotizen
          geschrieben.
        </p>
        {previewLoading ? (
          <p className="text-sm text-label-tertiary">Gebuchte Stunden werden gelesen …</p>
        ) : null}
        {previewError ? (
          <div className="space-y-2">
            <p className="text-sm text-status-red">{previewError}</p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setPreviewNonce((value) => value + 1)}
            >
              Erneut laden
            </Button>
          </div>
        ) : null}
        {hasHours && preview ? (
          <fieldset className="space-y-2">
            <legend className="eh-label">{hoursQuestion(preview)}</legend>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio"
                name="po-reject-stunden"
                className="mt-1"
                checked={action === "umbuchen"}
                disabled={loading}
                onChange={() => setAction("umbuchen")}
              />
              <span className="font-medium text-label">
                Die Stunden werden auf ein anderes Projekt umgebucht.
              </span>
            </label>
            {action === "umbuchen" ? (
              <div className="pl-6">
                <Autocomplete<AuftragLookup>
                  key={`${projectId}-${previewNonce}-${open ? "open" : "closed"}`}
                  label="Zielprojekt"
                  placeholder="Nummer, Kunde oder Ort …"
                  minChars={0}
                  required
                  loadOptions={async (query) => {
                    const list = query.trim()
                      ? await searchAuftraege(query)
                      : await getRecentAuftraege({ limit: 15 });
                    return list
                      .filter(
                        (row) =>
                          row.id !== projectId &&
                          String(row.workflow_status || "").trim().toLowerCase() !== "abgelehnt",
                      )
                      .map((row) => ({
                        key: String(row.id),
                        label: formatAuftragLookupLabel(row),
                        description: row.beschreibung || undefined,
                        value: row,
                      }));
                  }}
                  onSelect={(option) => setTarget(option?.value ?? null)}
                />
              </div>
            ) : null}
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio"
                name="po-reject-stunden"
                className="mt-1"
                checked={action === "verfallen"}
                disabled={loading}
                onChange={() => {
                  setAction("verfallen");
                  setTarget(null);
                }}
              />
              <span className="font-medium text-label">
                Die Stunden bleiben auf dem Projekt und verfallen mit.
              </span>
            </label>
          </fieldset>
        ) : null}
        <div className="space-y-1">
          <label className="eh-label" htmlFor="po-reject-grund">
            Begründung<span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="po-reject-grund"
            className="eh-input min-h-[5rem]"
            rows={4}
            placeholder="Bitte kurz begründen …"
            value={begruendung}
            disabled={loading}
            onChange={(e) => setBegruendung(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}
